import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Instagram, Youtube } from 'lucide-react';
import { TikTokIcon, SpotifyIcon, ThreadsIcon } from '@/components/icons';
import { WordsPullUp } from '@/components/animations';

// Same dark radial vignette as the Links section — content floats in the world,
// no card.
const VIGNETTE =
  'radial-gradient(ellipse 90% 80% at 50% 50%, rgba(13,12,11,0.9) 0%, rgba(13,12,11,0.62) 55%, transparent 100%)';

const PLATFORMS = [
  { name: 'Instagram', handle: '@joeurbinati', href: 'https://instagram.com/joeurbinati', Icon: Instagram },
  { name: 'TikTok', handle: '@joeurbinati', href: 'https://tiktok.com/@joeurbinati', Icon: TikTokIcon },
  { name: 'YouTube', handle: '@joeurbinati', href: 'https://youtube.com/@joeurbinati', Icon: Youtube },
  { name: 'Threads', handle: '@joeurbinati', href: 'https://www.threads.com/@joeurbinati', Icon: ThreadsIcon },
];

const DEEP = [
  { name: 'Instagram', handle: '@thekingbelow', href: 'https://instagram.com/thekingbelow', Icon: Instagram },
  { name: 'Spotify', handle: 'TKB Soundtrack', href: 'https://open.spotify.com/search/the%20king%20below', Icon: SpotifyIcon },
];

// Quiet Space Mono section label over a hairline that fades to the right.
// `tone` swaps the amber world-accent for the ember (King Below) one.
const Label = ({ children, tone = 'ash' }) => (
  <div className="mb-4 flex items-center gap-4">
    <span
      className={`font-mono text-[10px] uppercase tracking-[0.34em] ${
        tone === 'ember' ? 'text-ember-soft' : 'text-cream/75'
      }`}
    >
      {children}
    </span>
    <span
      className={`h-px flex-1 bg-gradient-to-r to-transparent ${
        tone === 'ember' ? 'from-ember-soft/30' : 'from-cream/12'
      }`}
    />
  </div>
);

// An editorial social row: muted glyph, name + handle, and a thin world-accent
// rule that draws across on hover. `accent` picks amber vs ember.
const SocialRow = ({ Icon, name, handle, href, active, delay, accent = 'amber', testId }) => {
  const ember = accent === 'ember';
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      initial={{ opacity: 0, y: 14 }}
      animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
      transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex items-center gap-4 py-5"
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border bg-black/20 transition-colors duration-300 ${
          ember
            ? 'border-ember/25 text-ember-soft'
            : 'border-cream/10 text-cream/70 group-hover:text-[#c17a2a]'
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1 transition-transform duration-500 ease-out group-hover:translate-x-1.5">
        <span className="block font-sans text-base font-medium tracking-tight text-cream transition-colors duration-300 group-hover:text-cream-warm">
          {name}
        </span>
        <span className="mt-0.5 block font-sans text-sm text-cream/55">{handle}</span>
      </span>
      <ArrowUpRight
        className={`h-5 w-5 shrink-0 text-cream/25 transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${
          ember ? 'group-hover:text-ember-soft' : 'group-hover:text-[#c17a2a]'
        }`}
      />
      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-cream/[0.08]" />
      <span
        className={`pointer-events-none absolute bottom-0 left-0 h-px w-0 transition-all duration-700 ease-out group-hover:w-full ${
          ember ? 'bg-ember-soft/70' : 'bg-[#c17a2a]/70'
        }`}
      />
    </motion.a>
  );
};

export const SocialCard = ({ active }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={active ? { opacity: 1 } : { opacity: 0 }}
    transition={{ duration: 0.7, ease: 'easeOut' }}
    className="w-[min(620px,90vw)] p-7 sm:p-12"
    style={{ background: VIGNETTE }}
    data-testid="social-card"
  >
    {/* Section identity */}
    <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.4em] text-[#c17a2a]/80">Find Me</p>
    <h2 className="font-display text-[2rem] leading-[1.08] text-cream sm:text-[2.6rem]">
      <WordsPullUp text="Everywhere I post." active={active} />
    </h2>
    <div className="mt-6 h-px w-full bg-gradient-to-r from-cream/15 via-cream/5 to-transparent" />

    {/* Main channels */}
    <div className="mt-10">
      <Label>Channels</Label>
      <div className="-mt-5">
        {PLATFORMS.map((p, i) => (
          <SocialRow
            key={p.name}
            Icon={p.Icon}
            name={p.name}
            handle={p.handle}
            href={p.href}
            active={active}
            delay={0.15 + i * 0.08}
            testId={`social-${p.name.toLowerCase().replace(/[^a-z]/g, '')}`}
          />
        ))}
      </div>
    </div>

    {/* The King Below — the fantasy world, ember-accented */}
    <div className="mt-10">
      <Label tone="ember">The King Below</Label>
      <div className="-mt-5">
        {DEEP.map((p, i) => (
          <SocialRow
            key={p.handle}
            Icon={p.Icon}
            name={p.name}
            handle={p.handle}
            href={p.href}
            active={active}
            delay={0.25 + i * 0.08}
            accent="ember"
            testId={`social-deep-${p.name.toLowerCase()}`}
          />
        ))}
      </div>
    </div>
  </motion.div>
);
